import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';

import * as types from '../../../../../types';
import { getDomainConfig } from '../../../../../lib/domainConfig';
import styles from './ProductInfoV2.module.scss';
import HideChatWidget from '../../../../../components/ZendeskChat/HideChatWidget';
import ImageGallery from '../../../../../components/ImageGallery/ImageGallery';
import {
  getCartSKUs,
  getSKUForCartItem,
  getSKUOptionDetails,
  getSupplementKeyBenefits,
  PurchaseContainer,
  PurchaseType,
  SKUOption,
  SKUType,
} from '../../../../ManageSubscription/Supplements/components/UpsellV2/UpsellV2';
import { formattedCentsToDollars, generateID, getLoadingOrErrorElement } from '../../../../../lib/util';
import {
  addMultipleSupplementSubscriptionsMutation,
  supplementsSubscriptionsQuery,
} from '../../../../../graphql-operations';
import useCheckoutPaths from '../../../../../hooks/useCheckoutPaths';
import * as events from '../../../../../lib/analytics/events';
import { ReactComponent as CheckIcon } from '../../../../../assets/images/icons/check.svg';
import { ReactComponent as InfoIcon } from '../../../../../assets/images/icons/icon_info.svg';
import InAppCheckoutModal from '../../../../ManageSubscription/Supplements/components/InAppCheckoutModal/InAppCheckoutModal';
import { getFiGrowthBook } from '../../../../../lib/growthbook';
import { useSelector } from 'react-redux';
import { HideCookieConsentBanner } from '../../../../../lib/oneTrust';

export interface ProductInfoV2Props {
  originalCart: types.Cart;
  originalCartPricing: types.CartPricing;
  originalOrderId: string;
}

export default function ProductInfoV2({ originalCart, originalCartPricing, originalOrderId }: ProductInfoV2Props) {
  const { t } = useTranslation();
  const [currentCarouselImage, setCurrentCarouselImage] = useState<number>(0);
  const [selectedSKUType, setSelectedSKUType] = useState<SKUType>(SKUType.Formula);
  const purchaseType = PurchaseType.Autoship;
  const [autoshipFrequency, setAutoshipFrequency] = useState<number>(6);
  const [cartModalOpen, setCartModalOpen] = useState<boolean>(false);
  const history = useHistory();
  const checkoutPaths = useCheckoutPaths();
  const fiveDollarFirstBagOfferLiveWeb = getFiGrowthBook().getFeatureValue<boolean>(
    'supplements-five-dollar-first-bag',
    false,
  );
  const freeBagOfferLive = useSelector((state: types.AppState) => state.config.siteConfig.firstBagFree ?? false);

  const goToThankYouPage = useCallback(
    (orderCart: types.Cart, invoiceNumber: string, cartPricing: types.CartPricing) => {
      history.replace(checkoutPaths.ThankYou, {
        orderId: invoiceNumber,
        orderedCart: orderCart,
        orderedCartPricing: cartPricing,
      });
    },
    [checkoutPaths.ThankYou, history],
  );

  const {
    data: subscriptionData,
    loading: subscriptionLoading,
    error: subscriptionError,
  } = useQuery<types.gqlTypes.ECOMMERCE_getSupplementSubscriptionsQuery>(supplementsSubscriptionsQuery, {
    fetchPolicy: 'cache-and-network',
  });

  const [purchaseMutation] = useMutation<
    types.gqlTypes.ECOMMERCE_addMultipleSupplementSubscriptions,
    types.gqlTypes.ECOMMERCE_addMultipleSupplementSubscriptionsVariables
  >(addMultipleSupplementSubscriptionsMutation, {
    refetchQueries: [{ query: supplementsSubscriptionsQuery }],
    onCompleted: (mutationData) => {
      const updatedCart = {
        ...originalCart,
        cartItems: {
          ...originalCart.cartItems,
          [generateID()]: {
            cartItemId: generateID(),
            lineItem: {
              lineItemId: generateID(),
              sku: getSKUForCartItem({
                skuType: selectedSKUType,
                purchaseType: purchaseType,
                eligibleForDiscount: true,
                frequency: autoshipFrequency,
              }),
            },
            quantity: 1,
          },
        },
      };
      fiveDollarFirstBagOfferLiveWeb
        ? events.supplements.upsellV2.experiment.confirm()
        : events.supplements.upsellV2.confirm();
      goToThankYouPage(updatedCart, originalOrderId, originalCartPricing);
    },
  });

  useEffect(() => {
    events.supplements.upsellV2.pageLoad();
  }, []);

  useEffect(() => {
    if (purchaseType === PurchaseType.Autoship) {
      events.supplements.upsellV2.autoshipOptionClicked();
    } else if (purchaseType === PurchaseType.OneTime) {
      events.supplements.upsellV2.buyOnceOptionClicked();
    }
  }, [purchaseType]);

  const loadingOrErrorElement = getLoadingOrErrorElement(subscriptionLoading, subscriptionError);
  if (loadingOrErrorElement) {
    return loadingOrErrorElement;
  }

  const isEligibleForFreeBag =
    !fiveDollarFirstBagOfferLiveWeb &&
    freeBagOfferLive &&
    !subscriptionData?.currentUser.hasPurchasedSupplementSubscription;

  const onNotNow = () => {
    fiveDollarFirstBagOfferLiveWeb
      ? events.supplements.upsellV2.experiment.noThanks()
      : events.supplements.upsellV2.notNow();
    goToThankYouPage(originalCart, originalOrderId, originalCartPricing);
  };

  return (
    <>
      <div className={styles.container}>
        <h1 className={styles.supplementsText}>
          <Trans
            i18nKey="choose_your_free_fi_supplement"
            components={{
              span: <span />,
              br: <br />,
            }}
          />
        </h1>
        <p className={styles.pitch}>
          {t('trusted_by_thousands_of_fi_dogs_to_help_them_stay_active_feel_their_best_and_live_well')}
          <strong>
            {fiveDollarFirstBagOfferLiveWeb && (
              <span className={classNames(styles.specialOffer)}>
                <Trans
                  i18nKey="special_offer_first_bag_only_5"
                  components={{
                    value: <span className={styles.specialOfferValue} />,
                  }}
                />
              </span>
            )}
            {isEligibleForFreeBag && false && (
              <span className={classNames(styles.specialOffer)}>
                <Trans
                  i18nKey="limited_time_offer_first_bag_is_free"
                  components={{
                    value: <span className={styles.specialOfferValue} />,
                  }}
                />
              </span>
            )}
          </strong>
        </p>
        <div
          className={classNames(styles.imageGalleryContainer, styles.hasNotification, {
            'ecom-flow-revamp-light': currentCarouselImage === 2,
          })}
        >
          <ImageGallery
            media={[
              {
                type: 'image',
                url: `/product_images/supplements/upsell-v2/supplements-in-app-1.png`,
                retinaWidth: 345,
                retinaHeight: 258,
              },
              {
                type: 'image',
                url: `/product_images/supplements/upsell-v2/supplements-in-app-2.png`,
                retinaWidth: 345,
                retinaHeight: 258,
              },
              {
                type: 'image',
                url: `/product_images/supplements/upsell-v2/supplements-in-app-3.png`,
                retinaWidth: 345,
                retinaHeight: 258,
              },
              {
                type: 'image',
                url: `/product_images/supplements/upsell-v2/supplements-in-app-4.png`,
                retinaWidth: 345,
                retinaHeight: 258,
              },
              {
                type: 'image',
                url: `/product_images/supplements/upsell-v2/supplements-in-app-5.png`,
                retinaWidth: 345,
                retinaHeight: 258,
              },
            ]}
            showMobileGallerySelector={false}
            wrapAround
            squareAspectRatio
            pageDots={true}
            onChange={(index: number) => setCurrentCarouselImage(index)}
          />
        </div>
        <div className={styles.skuOptionSelector}>
          {Object.values(SKUType).map((skuType) => (
            <SKUOption
              key={skuType}
              skuType={skuType}
              selectedSKUType={selectedSKUType}
              onSelect={setSelectedSKUType}
              fromInsights={undefined}
              recommended={undefined}
              imageOnly
            />
          ))}
        </div>
        <h1 className={styles.skuOptionTitle}>{getSKUOptionDetails(t)[selectedSKUType].title}</h1>
        <div className={styles.skuOptionDetails}>
          {getSupplementKeyBenefits(t)[selectedSKUType].map(
            (benefit: { image: JSX.Element; title: string; description: string }, index: number) => (
              <div key={index} className={styles.skuOptionKeyBenefitItem}>
                <div className={styles.skuOptionKeyBenefitItemImageContainer}>{benefit.image}</div>
                <div className={styles.skuOptionKeyBenefitItemText}>
                  <div className={styles.skuOptionKeyBenefitItemTitle}>{benefit.title}</div>
                  <div className={styles.skuOptionKeyBenefitItemDescription}>{benefit.description}</div>
                </div>
              </div>
            ),
          )}
        </div>
        <PurchaseContainer
          purchaseType={purchaseType}
          autoshipFrequency={autoshipFrequency}
          setAutoshipFrequency={setAutoshipFrequency}
          isNextItemEligibleForFirstBagDiscount={true}
          setPurchaseType={() => {}}
          autoshipOnly
          fiveDollarFirstBagOfferLive={fiveDollarFirstBagOfferLiveWeb}
          isEligibleForFreeBag={isEligibleForFreeBag}
        />
        <HideChatWidget />
        <HideCookieConsentBanner />
        <div className={styles.termsAndConditionsContainer}>
          <div className={styles.termsAndConditionsIcon}>
            <InfoIcon />
          </div>
          <div className={styles.termsAndConditionsTitle}>
            <Trans
              i18nKey="by_clicking_place_order_you_agree_to_our_terms_and_conditions"
              components={{
                tosLink: (
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  <a
                    href={`https://${getDomainConfig().baseDomain}/tos`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Terms and Conditions"
                  />
                ),
              }}
            />
          </div>
        </div>
        <div className={styles.recurringSubscriptionContainer}>
          <div className={styles.recurringSubscriptionIcon}>
            <CheckIcon />
          </div>
          <div className={styles.recurringSubscriptionTitle}>
            {fiveDollarFirstBagOfferLiveWeb
              ? t('recurring_subscription_will_start_with_shipment_of_2nd_bag_for_25_per_bag')
              : t('recurring_subscription_starts_with_shipment_of_2nd_bag')}
          </div>
        </div>
      </div>
      <div className={styles.mobileCTAContainer}>
        <div className={styles.mobileCTAButtons}>
          <div className={styles.inAppCheckoutContainer}>
            <InAppCheckoutModal
              open={cartModalOpen}
              onOpen={() => {
                fiveDollarFirstBagOfferLiveWeb
                  ? events.supplements.upsellV2.experiment.tryItWeeklyOptionClicked()
                  : events.supplements.upsellV2.tryItWeeklyOptionClicked();
              }}
              onClose={() => setCartModalOpen(false)}
              cartItems={[
                {
                  skuType: selectedSKUType,
                  purchaseType: purchaseType,
                  eligibleForDiscount: true,
                  frequency: autoshipFrequency,
                },
              ]}
              trigger={
                <div className={styles.addToCartContainer}>
                  {/* web try it button */}
                  <button className={styles.confirmOrderButton}>
                    {fiveDollarFirstBagOfferLiveWeb ? t('get_it') : t('try_it_for_free')}
                  </button>
                </div>
              }
              onPurchase={() => {
                purchaseMutation({
                  variables: {
                    input: {
                      skusToAdd: getCartSKUs([
                        {
                          skuType: selectedSKUType,
                          purchaseType: purchaseType,
                          eligibleForDiscount: true,
                          frequency: autoshipFrequency,
                        },
                      ]),
                      forceApplyDiscount: fiveDollarFirstBagOfferLiveWeb,
                    },
                  },
                });
              }}
              fiveDollarFirstBagOfferLive={fiveDollarFirstBagOfferLiveWeb}
              isEligibleForFreeBag={isEligibleForFreeBag}
              isUpsellLayout
            />
          </div>
          <button className={styles.notNowButton} onClick={() => onNotNow()}>
            {t('no_thanks')}
          </button>
        </div>
        {(fiveDollarFirstBagOfferLiveWeb || isEligibleForFreeBag) && (
          <div className={styles.mobileCTAFooter}>
            {fiveDollarFirstBagOfferLiveWeb ? (
              t('you_will_be_charged_amount_today_and_will_renew_every_weeks', {
                amount: formattedCentsToDollars(500),
                weeks: autoshipFrequency,
                total: formattedCentsToDollars(2500),
              })
            ) : (
              <Trans
                i18nKey="you_will_be_charged_amount_today_and_will_renew_at_per_week_billed_as_total_every_weeks"
                values={{
                  amount: formattedCentsToDollars(0),
                  perWeek: formattedCentsToDollars(2500 / autoshipFrequency),
                  total: formattedCentsToDollars(2500),
                  weeks: autoshipFrequency,
                }}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}
